import { getConsolidatedTranslations } from 'core/utils/parsers';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';

import { Box, FormControl, FormControlLabel, Grid, Paper, Radio, RadioGroup, SpeedDial, SpeedDialAction, Typography, useTheme } from "@mui/material";
import { isEmptyOrUndefined } from "@zippeditoolsjs/blocks";
import { IconComponent } from "@zippeditoolsjs/zippedi-icons";

import CloseButton from "../../tools/CloseButton";
import MapTypeToggle from '../../tools/MapTypeToggle';
import StoreMap from '../../tools/StoreMap';
import { colorBasedOnTheme, highlightedProductColor, mapBackgroundColor } from "../../utils/colors";

const RADIUSPLUS = 4,
  INTERVALAISLE = 3;
export default function InteractiveMap(props) {
  const {
    chain,
    hasRobot,
    storeMap,
    storeCategories,
    taskType,
    setIsSidenavOpen,
    selectedAlert,
    setSelectedAlert,
    setAisleLandmarkMap,
    setAislesList,
    currentFloor,
    setCurrentFloor,
    setRefresh,
    heightRef,
    isHoverActive = true,
    isCLickActive = true,
    canShowActions = true,
    selectedAisle = {},
    selectedDots = [],
    highlightedBoxes = [],
    isZoomActive,
    visualizationMode: inputVisualizationMode = 'ds_aisle',
    taskData,
    mapType,
    setMapType,
    hasEnhancedMap,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const [isHoverOn, setIsHoverOn] = useState(false);
  const [categoriesOnMap, setCategoriesOnMap] = useState({});
  const [selectedAlertIndex, setSelectedAlertIndex] = useState(null);
  const [selectedElement, setSelectedElement] = useState({});
  const [isTooltipFixed, setIsTooltipFixed] = useState(false);
  const [landmarkMap, setLandmarkMap] = useState({})
  const [taskDataGrouped, setTaskDataGrouped] = useState([])
  // Speed dial states
  const [visualizationMode, setVisualizationMode] = useState(inputVisualizationMode);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);
  const [openLabel, setOpenLabel] = useState(false);
  const [openFloor, setOpenFloor] = useState(false);
  const speedDialRef = useRef(null);
  const labelRef = useRef(null);
  const floorRef = useRef(null);

  // Highlighted element
  const [highlightStyle, setHighlightStyle] = useState({
    height: '20px',
    width: '20px',
    top: 0,
    left: 0,
    backgroundColor: '#ffa6004b',
    position: 'absolute',
    borderRadius: '50%',
    border: `2px ${highlightedProductColor()} solid`,
    opacity: 0,
    zIndex: -1,
    transition: 'opacity 0.2s',
  });
  const [isTooltipPined, setIsTooltipPined] = useState(false);

  const selectedAlerts = useState({})[0];

  // Lyfecycle methods

  useEffect(() => {
    if (taskData?.length) {
      const categoryAux = mapCategoriesLayout(storeMap.map_info, taskData, taskType === 'promotions');
      setCategoriesOnMap(categoryAux);
    }
  }, [storeMap])

  // Hook that closes the speed dial or the filters depending on clicks outside of the passed ref
  useEffect(() => {
    // Close speed dial if clicked on outside of element
    function handleClickOutside(event) {
      if (labelRef.current && !labelRef.current.contains(event.target)) {
        setOpenLabel(false);
      } else if (floorRef.current && !floorRef.current.contains(event.target)) {
        setOpenFloor(false);
      } else if (!labelRef.current && !floorRef.current && speedDialRef.current && !speedDialRef.current.contains(event.target)) {
        setOpenSpeedDial(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [speedDialRef, labelRef, floorRef]);

  useEffect(() => {
    if (selectedAlert) {
      setSelectedElement(selectedAlert);
      setIsTooltipPined(true);
      setIsSidenavOpen(true);
      setSelectedAlert(selectedAlert)
      setTimeout(() => {
        highlightElement(selectedAlert);
        const [xElement, yElement] = selectedAlert.map_point_scaled;
        moveTooltipCanvas({ mode: 'moveOn', coord: [xElement, yElement] });
      }, 150);
    }
  }, [selectedAlert]);

  // Methods
  const mapCategoriesLayout = (mapInfo, taskTableInfo, parseCategories = false) => {
    const categoryAux = {};
    if (storeCategories?.length > 1) {
      const scale_valueX = mapInfo.scale_value[0],
        scale_valueY = mapInfo.scale_value[1],
        category_i = parseCategories ? `store_category_${storeCategories[1].level}` : `category_${storeCategories[1].level}`;

      storeCategories.forEach(categoryInfo => {
        categoryAux[categoryInfo.value] = { name: categoryInfo.category_name };
      })
      taskTableInfo.forEach(detectionInfo => {
        if (detectionInfo.map_point) {
          // separating darkStore of the rest of store to have two categories on the map
          let categoryValue, categoryInfo;
          if (detectionInfo.aisle?.includes('DS')) {
            categoryValue = `${detectionInfo[category_i]}-DS`;
            categoryInfo = categoryAux[categoryValue];
            // TODO: check why something not come the name of the category like: J508 27-07-2022
            if (!categoryInfo) {
              if (categoryAux[detectionInfo[category_i]]?.name) {
                categoryAux[categoryValue] = { name: categoryAux[detectionInfo[category_i]].name }
                // categoryAux[categoryValue] = { name: 'No detectado' }
              }
            };
          } else {
            categoryValue = detectionInfo[category_i];
            categoryInfo = categoryAux[categoryValue];
          }

          const map_pointX = detectionInfo.map_point[0],
            map_pointY = detectionInfo.map_point[1];
          if (categoryInfo && categoryInfo.map_point) {
            const totalAlerts = categoryInfo.totalAlerts;
            categoryInfo.totalAlerts++;
            categoryInfo.map_point[0] = (categoryInfo.map_point[0] * (totalAlerts - 1) + map_pointX * scale_valueX) / totalAlerts;
            categoryInfo.map_point[1] = (categoryInfo.map_point[1] * (totalAlerts - 1) + map_pointY * scale_valueY) / totalAlerts;
          } else if (categoryInfo) {
            categoryInfo.map_point = [map_pointX * scale_valueX, map_pointY * scale_valueY];
            categoryInfo.totalAlerts = 1;
          }
        }
      });
    }
    return categoryAux;
  }

  const moveTooltipCanvas = ({ mode, coord, isTooltipFixed = false }) => {
    const tooltipCanvas = document.getElementById('tooltipCanvas');
    const responsiveCanvas = document.getElementById('responsive-canvas');
    if (mode === 'moveOn' && tooltipCanvas) {
      setIsTooltipFixed(isTooltipFixed);
      const [xElement, yElement] = coord;
      // Scale the alert coordinates for the tooltip
      const width = responsiveCanvas.offsetWidth
      const height = responsiveCanvas.offsetHeight
      const original_width = storeMap?.map_info?.width
      const original_height = storeMap?.map_info?.height
      const scaled_x = xElement * width / original_width
      const scaled_y = yElement * height / original_height
      const [newLeft, newTop] = [`${scaled_x + 30}px`, `${scaled_y + 50}px`]; // + 30/50 so the tooltip is in the correct position
      tooltipCanvas.style.left = newLeft;
      tooltipCanvas.style.top = newTop;
      tooltipCanvas.style.opacity = '1';
      tooltipCanvas.style.zIndex = '1';
    } else if (mode === 'close' && tooltipCanvas) {
      setIsTooltipFixed(false);
      tooltipCanvas.style.opacity = '0';
      tooltipCanvas.style.zIndex = '-1';
    } else if (tooltipCanvas) {
      tooltipCanvas.style.opacity = '0';
      tooltipCanvas.style.zIndex = '-1';
    }
  }

  const getMousePos = (event, parsedTaskData) => {
    const aislesClientNames = storeMap.map_info.client_linear_names;
    const x = event.clientX,
      y = event.clientY;
    let notFound = true;
    for (let index = 0; index < parsedTaskData.taskDataGrouped.length; index++) {
      const element = parsedTaskData.taskDataGrouped[index];
      const [xElement, yElement] = element.map_point_scaled;
      const radius = element.radius;
      const mouseIn = xElement - (radius + RADIUSPLUS) <= x && x <= xElement + (radius + RADIUSPLUS) && yElement - (radius + RADIUSPLUS) <= y && y <= yElement + (radius + RADIUSPLUS);
      if (mouseIn && (!isHoverOn || index !== selectedAlertIndex)) {
        notFound = false;
        setIsHoverOn(true);
        setSelectedAlertIndex(index);

        // add client aisle name
        const aisleName = element.aisle_name
        const clientAisleName = aislesClientNames.hasOwnProperty(aisleName) ? aislesClientNames[aisleName] : aisleName;
        element["client_aisle_name"] = clientAisleName
        element["hovered"] = true
        setSelectedElement(element);
        setLandmarkMap(parsedTaskData.aisleLandmarkMap)
        setTaskDataGrouped(parsedTaskData.taskDataGrouped)
        moveTooltipCanvas({ mode: 'moveOn', coord: [xElement, yElement] });
        break;
      } else if (mouseIn && isHoverOn) {
        notFound = false;
        break;
      }
    }
    if (notFound) {
      setSelectedElement({ ...selectedElement, hovered: false })
      if (!isTooltipPined) {
        setSelectedElement({ index: undefined, groupName: undefined, aisle_name: undefined, client_aisle_name: undefined, landmark: undefined, map_point: undefined, tramo: undefined, radius: undefined, completed: undefined, totalAlerts: undefined });
        if (!isTooltipFixed) { moveTooltipCanvas({}) };
        setIsHoverOn(false);
      }
    }
  }

  const highlightElement = (element, isActive = true) => {
    if (isActive) {
      const responsiveCanvas = document.getElementById('responsive-canvas');
      const [xElement, yElement] = element.map_point_scaled;
      // Scale the alert coordinates for the tooltip
      const width = responsiveCanvas.offsetWidth
      const height = responsiveCanvas.offsetHeight
      const original_width = storeMap?.map_info?.width
      const original_height = storeMap?.map_info?.height
      const scaled_x = xElement * width / original_width
      const scaled_y = yElement * height / original_height
      const [newLeft, newTop] = [scaled_x + 16.5, scaled_y + 56]; // + 30/50 so the tooltip is in the correct position

      const highlighterRadius = element.radius + RADIUSPLUS;
      const newStyle = {
        ...highlightStyle
        , left: `${newLeft - highlighterRadius}px`, top: `${newTop - highlighterRadius}px`
        , opacity: '1', zIndex: '1'
        , height: `${2 * highlighterRadius}px`, width: `${2 * highlighterRadius}px`
      }
      setHighlightStyle(newStyle);
    } else {
      setHighlightStyle({ ...highlightStyle, opacity: '0', zIndex: '-1' });
    }
  }

  const handleCanvasClick = (event, forceCloseTooltip = false) => {
    if (selectedElement?.hovered && !forceCloseTooltip) {
      setIsTooltipPined(true);
      setIsSidenavOpen(true);
      setSelectedAlert(selectedElement)
      setAisleLandmarkMap(landmarkMap)
      setAislesList(taskDataGrouped)
      setTimeout(() => {
        highlightElement(selectedElement);
        const [xElement, yElement] = selectedElement.map_point_scaled;
        moveTooltipCanvas({ mode: 'moveOn', coord: [xElement, yElement] });
      }, 150);
    } else {
      setIsTooltipPined(false);
      setSelectedElement({ hovered: false, index: undefined, groupName: undefined, aisle_name: undefined, client_aisle_name: undefined, landmark: undefined, map_point: undefined, tramo: undefined, radius: undefined, completed: undefined, totalAlerts: undefined });
      setIsHoverOn(false);
      moveTooltipCanvas({});
      highlightElement(selectedElement, false);
      if (!forceCloseTooltip) setIsSidenavOpen(false);
    }
  }

  const handleCanvasHover = (event, drawerReturn) => {
    getMousePos(event, drawerReturn);
  }

  // Speed dial actions and handlers
  const actions = [
    { icon: <IconComponent iconName={'filter'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.label_filter', 'Label filter'), operation: 'label' },
    { icon: <IconComponent iconName={'map'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.change_floor', 'Change floor'), operation: 'floor' },
    { icon: <IconComponent iconName={'refresh'} style={{ fontSize: '20px' }} />, name: t('cws_app.digital_store.refresh_map', 'Refresh map'), operation: 'refresh' },
  ];

  const handleSpeedDialOpen = () => setOpenSpeedDial(true);

  const handleSpeedDialActionClick = (e, operation) => {
    e.preventDefault();
    setOpenSpeedDial(true);
    switch (operation) {
      case 'label':
        setOpenLabel(true);
        setOpenFloor(false);
        break;
      case 'floor':
        setOpenLabel(false);
        setOpenFloor(true);
        break;
      case 'refresh':
        setRefresh(true);
        break;
      default:
        break;
    }
  }

  // Label Selection
  const handleLabelChange = (event) => {
    setVisualizationMode(event?.target?.value)
  }

  // Floor Selection
  const handleFloorChange = (event) => {
    const currentFloorObj = storeMap.sessions_by_floor[Number(event?.target?.value)]
    setCurrentFloor(currentFloorObj)
  }

  return (
    <Paper ref={heightRef} style={{ position: 'relative', width: '100%', height: 'fit-content', padding: '0 1em', backgroundColor: mapBackgroundColor() }}>
      {/* Speed dial and custom tooltips */}
      {openLabel &&
        <Paper ref={labelRef} sx={{ position: 'absolute', right: '1em', top: '4em', padding: 1, zIndex: 99, pointerEvents: 'auto' }}>
          <FormControl>
            <RadioGroup
              aria-labelledby="label-filter"
              name="label-filter"
              value={visualizationMode}
              onChange={handleLabelChange}
            >
              <FormControlLabel value="ds_basic" control={<Radio size='small' />} label={t('cws_app.general.none', "None")} />
              <FormControlLabel value="ds_aisle" control={<Radio size='small' />} label={t('cws_app.general.Aisles', "Aisles")} />
              <FormControlLabel value="ds_category" control={<Radio size='small' />} label={t('cws_app.general.categories', "Cateogries")} />
            </RadioGroup>
          </FormControl>
        </Paper>
      }
      {openFloor &&
        <Paper ref={floorRef} sx={{ position: 'absolute', right: '5em', top: '4em', padding: 1, zIndex: 99, pointerEvents: 'auto' }}>
          <FormControl>
            <RadioGroup
              aria-labelledby="floor-selection"
              name="floor-selection"
              value={currentFloor?.floor_number}
              onChange={handleFloorChange}
            >
              {Object.values(storeMap.sessions_by_floor).map((floor) => {
                return (
                  <FormControlLabel value={floor.floor_number} key={floor?.floor_number} control={<Radio size='small' />} label={
                    t('cws_app.digital_store.sel_floor_i', 'Floor {{floorIndex}}', { floorIndex: floor?.floor_number })
                  }
                  />
                )
              })}
            </RadioGroup>
          </FormControl>
        </Paper>
      }
      <Grid container direction='row' justifyContent='space-between' alignItems='flex-end' sx={{ transform: 'translateZ(0px)', flexGrow: 1, zIndex: 10, position: 'relative' }}>
        <Box sx={{ display: 'flex', mb: 1, ml: 1 }}>
          <MapTypeToggle mapType={mapType} setMapType={setMapType} hasEnhancedMap={hasEnhancedMap} />
        </Box>
        {canShowActions &&
          <Box display='flex' justifyContent='flex-end' alignItems='flex-end'>
            <SpeedDial
              ref={speedDialRef}
              ariaLabel="Map configurations"
              FabProps={{ size: 'small' }}
              icon={<IconComponent iconName={'settings'} style={{ fontSize: '20px' }} />}
              direction="left"
              open={openSpeedDial}
              onClick={handleSpeedDialOpen}
            >
              {actions.map((action) => {
                return (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.name}
                    tooltipPlacement='top'
                    onClick={(e) => handleSpeedDialActionClick(e, action.operation)}
                  />
                )
              })}
            </SpeedDial>
          </Box>
        }
      </Grid>
      <StoreMap
        storeMap={storeMap.map_info}
        tasksData={taskData}
        groupingAlertsBy={storeMap.GROUPINGALERTSBY}
        categoriesOnMap={categoriesOnMap}
        visualizationMode={visualizationMode}
        handleCanvasClick={isCLickActive ? handleCanvasClick : () => { }}
        handleCanvasHover={isHoverActive ? handleCanvasHover : () => { }}
        selectedAisle={selectedAisle?.code}
        isZoomActive={isZoomActive}
        selectedDots={selectedDots}
        highlightedBoxes={highlightedBoxes}
        mapType={mapType}
        hasEnhancedMap={hasEnhancedMap}
      />

      <Box id="highlighter" style={highlightStyle}></Box>
      {(!isEmptyOrUndefined(selectedElement, 'object') || !isEmptyOrUndefined(selectedAlerts, 'object')) &&
        <Paper id="tooltipCanvas" sx={{ position: 'absolute', minWidth: '15em', p: 2, color: colorBasedOnTheme(theme, 'color-darkTheme'), backgroundColor: colorBasedOnTheme(theme, 'bgcolor-darkTheme') }}>
          {/* Map products tooltips */}
          {selectedElement?.aisle_name ?
            <Grid container direction='column' spacing={2}>
              <Grid container item justifyContent='flex-end' alignItems='center' sx={{ position: 'absolute', top: '-1em' }}>
                <CloseButton setIsOpen={() => handleCanvasClick({}, true)} isColorbgAlwaysDark={true} />
              </Grid>

              <Grid container item direction='column' alignItems='center' mt={1}>
                <Typography variant='h6'>{getConsolidatedTranslations(t, chain, 'landmark', 'digital_store', 'Landmark')} {selectedElement.landmark}</Typography>
                <Typography variant='subtitle2'>{t('cws_app.general.aisle', 'Aisle')} {selectedElement?.client_aisle_name ? selectedElement?.client_aisle_name : selectedElement?.aisle_name}</Typography>
              </Grid>
              <Grid item>
                <Typography variant='subtitle2'>{t('cws_app.digital_store.alerts', 'Alerts')}: {selectedElement.totalAlerts}</Typography>
                <Typography variant='subtitle2'>{t('cws_app.digital_store.complete_alerts', 'Completed alerts')}: {selectedElement.completed}</Typography>
              </Grid>
              {hasRobot && selectedElement.tramo &&
                <Grid item display='flex' justifyContent='space-between'>
                  <Typography variant='caption'>{t('cws_app.digital_store.linear', 'Linear')}: {(selectedElement.tramo - 1) * INTERVALAISLE}m - {selectedElement.tramo * INTERVALAISLE}m</Typography>
                </Grid>
              }
            </Grid>
            :
            <Grid container direction='column' spacing={2}>
              <Grid container item justifyContent='center'>
                <Typography variant='h6'>{t('cws_app.general.aisle', 'Aisle')} {selectedAlerts?.client_aisle_name ? selectedAlerts?.client_aisle_name : selectedAlerts?.aisle_name}</Typography>
              </Grid>
              <Grid item>
                <Typography variant='subtitle2'>{t('cws_app.digital_store.alerts', 'Alerts')}: {selectedAlerts.totalAlerts}</Typography>
                <Typography variant='subtitle2'>{t('cws_app.digital_store.complete_alerts', 'Completed alerts')}: {selectedAlerts.completed}</Typography>
              </Grid>
              {hasRobot && selectedAlerts.tramo ?
                <Grid item>
                  <Typography variant='caption'>{t('cws_app.digital_store.linear', 'Linear')}: {(selectedAlerts.tramo - 1) * INTERVALAISLE}m -{selectedAlerts.tramo * INTERVALAISLE}m</Typography>
                </Grid>
                :
                <Grid item>
                  <Typography variant='caption'>{getConsolidatedTranslations(t, chain, 'landmark', 'digital_store', 'Landmark')}: {selectedAlerts.landmark}</Typography>
                </Grid>
              }
            </Grid>
          }
        </Paper>
      }
    </Paper>
  );
}
